import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Button,
  message,
  Spin,
  Steps,
  Card,
  Checkbox,
  Result,
  Typography,
  Divider,
  Alert,
  Input,
  Form,
  Row,
  Col
} from 'antd';
import {
  FileTextOutlined,
  EditOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  HomeOutlined
} from '@ant-design/icons';
import SignatureCanvas from 'react-signature-canvas';
import { PDFDocument } from 'pdf-lib';
import { supabase } from '../../api/supabase';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

// Styles - Noir et Blanc élégant
const styles = {
  page: {
    minHeight: '100vh',
    background: '#f5f5f5',
    padding: '40px 20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  container: {
    maxWidth: '800px',
    width: '100%',
    background: '#fff',
    borderRadius: '16px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    border: '1px solid #e0e0e0',
  },
  header: {
    background: '#1a1a1a',
    padding: '32px',
    color: '#fff',
    textAlign: 'center',
  },
  headerTitle: {
    margin: 0,
    fontSize: '28px',
    fontWeight: 700,
    color: '#fff',
  },
  headerSubtitle: {
    margin: '8px 0 0',
    fontSize: '16px',
    color: 'rgba(255,255,255,0.7)',
  },
  content: {
    padding: '32px',
  },
  stepsContainer: {
    marginBottom: '32px',
  },
  section: {
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '16px',
    color: '#1a1a1a',
  },
  contractInfo: {
    background: '#f8f9fa',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid #eee',
  },
  signatureBox: {
    border: '2px solid #1a1a1a',
    borderRadius: '8px',
    background: '#ffffff',
    marginBottom: '16px',
  },
  signatureCanvas: {
    width: '100%',
    height: '200px',
  },
  checkboxGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '24px',
  },
  actionButton: {
    height: '56px',
    fontSize: '18px',
    fontWeight: 600,
    borderRadius: '12px',
    width: '100%',
  },
  footer: {
    textAlign: 'center',
    padding: '24px',
    background: '#f8f9fa',
    borderTop: '1px solid #eee',
  },
};

const ContractSignAndPay = () => {
  const { uniqueLink } = useParams();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'unified'; // 'unified', 'signature', 'payment'

  const signatureRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [processing, setProcessing] = useState(false);

  // Form state
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptCancellation, setAcceptCancellation] = useState(false);
  const [signatureData, setSignatureData] = useState(null);

  // Billing address state
  const [billingAddress, setBillingAddress] = useState({
    street: '',
    city: '',
    postalCode: '',
    country: 'France'
  });

  // Status
  const [signatureComplete, setSignatureComplete] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);

  useEffect(() => {
    loadContract();
  }, [uniqueLink]);

  const loadContract = async () => {
    setLoading(true);
    try {
      // Chercher le contrat par unique_link
      const { data, error } = await supabase
        .from('contract_workflows')
        .select('*')
        .eq('unique_link', uniqueLink)
        .single();

      if (error || !data) {
        message.error('Contrat non trouvé ou lien expiré');
        setContract(null);
        return;
      }

      setContract(data);

      // Définir l'étape actuelle selon le statut
      if (data.status === 'signed' || data.signature_date) {
        setSignatureComplete(true);
        setCurrentStep(mode === 'unified' ? 1 : 0);
      }
      if (data.status === 'paid') {
        setPaymentComplete(true);
        setCurrentStep(2);
      }

      // Si mode signature uniquement et déjà signé
      if (mode === 'signature' && data.signature_date) {
        setCurrentStep(1); // Afficher confirmation
      }

    } catch (error) {
      console.error('Error loading contract:', error);
      message.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Validation de l'adresse de facturation
  const isAddressValid = () => {
    return billingAddress.street.trim() !== '' &&
           billingAddress.city.trim() !== '' &&
           billingAddress.postalCode.trim() !== '' &&
           billingAddress.country.trim() !== '';
  };

  const handleAddressChange = (field, value) => {
    setBillingAddress(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Convertir l'URL Google Drive en URL de téléchargement direct
  const getDirectDownloadUrl = (url) => {
    if (!url) return null;

    // Si c'est déjà une URL Supabase ou directe, la retourner
    if (url.includes('supabase') || url.endsWith('.pdf')) {
      return url;
    }

    // Convertir les liens Google Drive
    // Format: https://drive.google.com/file/d/FILE_ID/view
    // ou: https://docs.google.com/document/d/DOC_ID/...
    const driveMatch = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
    if (driveMatch) {
      const fileId = driveMatch[1];
      return `https://drive.google.com/uc?export=download&id=${fileId}`;
    }

    return url;
  };

  // Configuration de la position de signature selon le type de contrat
  // Page 2 pour les contrats bateaux (index 1)
  // Coordonnées en points PDF (origine en bas à gauche)
  // A4 = 595 x 842 points
  const getSignatureConfig = () => {
    // Configuration par défaut pour contrats bateaux
    // Tu peux ajuster ces valeurs après avoir mesuré sur ton PDF
    return {
      pageIndex: 1, // Page 2 (index commence à 0)
      x: 380,       // Position X depuis la gauche (ajuster selon ton modèle)
      y: 150,       // Position Y depuis le bas (ajuster selon ton modèle)
      width: 150,   // Largeur de la signature
    };
  };

  // Apposer la signature sur le PDF
  const signPdfWithSignature = async (pdfUrl, signatureDataUrl) => {
    try {
      // Télécharger le PDF (doit être sur Supabase pour éviter CORS)
      console.log('Téléchargement du PDF depuis:', pdfUrl);

      const response = await fetch(pdfUrl);
      if (!response.ok) {
        throw new Error(`Impossible de télécharger le PDF: ${response.status}`);
      }

      const pdfBytes = await response.arrayBuffer();

      // Charger le PDF avec pdf-lib
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pages = pdfDoc.getPages();

      // Obtenir la configuration de signature
      const config = getSignatureConfig();

      // Vérifier que la page existe
      const pageIndex = Math.min(config.pageIndex, pages.length - 1);
      const targetPage = pages[pageIndex];
      const { width, height } = targetPage.getSize();

      console.log(`📄 PDF: ${pages.length} pages, Page cible: ${pageIndex + 1}, Dimensions: ${width}x${height}`);

      // Convertir la signature base64 en image PNG
      const signatureImageBytes = await fetch(signatureDataUrl).then(res => res.arrayBuffer());
      const signatureImage = await pdfDoc.embedPng(signatureImageBytes);

      // Dimensions de la signature (proportionnelles)
      const signatureWidth = config.width;
      const signatureHeight = (signatureImage.height / signatureImage.width) * signatureWidth;

      // Position de la signature
      const signatureX = config.x;
      const signatureY = config.y;

      console.log(`✍️ Signature: X=${signatureX}, Y=${signatureY}, W=${signatureWidth}, H=${signatureHeight}`);

      // Dessiner la signature sur la page
      targetPage.drawImage(signatureImage, {
        x: signatureX,
        y: signatureY,
        width: signatureWidth,
        height: signatureHeight,
      });

      // Ajouter la date de signature sous la signature
      const signatureDate = dayjs().format('DD/MM/YYYY à HH:mm');
      targetPage.drawText(`Signé le ${signatureDate}`, {
        x: signatureX,
        y: signatureY - 15,
        size: 8,
      });

      // Sauvegarder le PDF modifié
      const signedPdfBytes = await pdfDoc.save();

      return signedPdfBytes;

    } catch (error) {
      console.error('Erreur lors de la signature du PDF:', error);
      // Si CORS ou autre erreur, on continue sans modifier le PDF
      return null;
    }
  };

  // Uploader le PDF signé vers Supabase Storage
  const uploadSignedPdf = async (pdfBytes, clientName) => {
    try {
      const fileName = `signed_contracts/${uniqueLink}_${clientName.replace(/\s+/g, '_')}_signed.pdf`;

      const { data, error } = await supabase.storage
        .from('documents')
        .upload(fileName, pdfBytes, {
          contentType: 'application/pdf',
          upsert: true
        });

      if (error) throw error;

      // Obtenir l'URL publique
      const { data: urlData } = supabase.storage
        .from('documents')
        .getPublicUrl(fileName);

      return urlData.publicUrl;

    } catch (error) {
      console.error('Erreur upload PDF signé:', error);
      return null;
    }
  };

  const handleSignature = async () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      message.warning('Veuillez signer dans le cadre ci-dessus');
      return;
    }

    if (!acceptTerms || !acceptCancellation) {
      message.warning('Veuillez accepter les conditions');
      return;
    }

    if (!isAddressValid()) {
      message.warning('Veuillez remplir votre adresse de facturation');
      return;
    }

    setProcessing(true);
    try {
      const signatureImage = signatureRef.current.toDataURL('image/png');
      let signedPdfUrl = null;

      // Essayer d'apposer la signature sur le PDF
      if (contract.pdf_url) {
        message.loading('Apposition de la signature sur le contrat...', 0);

        const signedPdfBytes = await signPdfWithSignature(contract.pdf_url, signatureImage);

        if (signedPdfBytes) {
          // Uploader le PDF signé vers Supabase
          signedPdfUrl = await uploadSignedPdf(signedPdfBytes, contract.client_name);

          if (signedPdfUrl) {
            message.destroy();
            message.success('Contrat signé et sauvegardé !');
          }
        } else {
          message.destroy();
          message.info('Signature enregistrée (PDF non modifiable à cause de restrictions)');
        }
      }

      // Sauvegarder dans la base de données
      const { error } = await supabase
        .from('contract_workflows')
        .update({
          signature_data: signatureImage,
          signature_date: new Date().toISOString(),
          signature_ip: await getClientIP(),
          terms_accepted: true,
          cancellation_accepted: true,
          billing_address: billingAddress,
          signed_pdf_url: signedPdfUrl, // URL du PDF signé sur Supabase
          status: 'signed'
        })
        .eq('unique_link', uniqueLink);

      if (error) throw error;

      setSignatureData(signatureImage);
      setSignatureComplete(true);
      message.success('Signature enregistrée !');

      // Passer à l'étape suivante
      if (mode === 'unified') {
        setCurrentStep(1); // Paiement
      } else {
        setCurrentStep(1); // Confirmation
      }

    } catch (error) {
      console.error('Error saving signature:', error);
      message.error('Erreur: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handlePayment = async () => {
    setProcessing(true);
    try {
      // Générer le lien de paiement VoxPay
      const paymentUrl = await generatePaymentLink();

      if (paymentUrl) {
        // Rediriger vers VoxPay
        window.location.href = paymentUrl;
      } else {
        message.error('Erreur lors de la génération du lien de paiement');
      }
    } catch (error) {
      console.error('Error generating payment:', error);
      message.error('Erreur: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const generatePaymentLink = async () => {
    // TODO: Intégrer VoxPay ici
    // Pour l'instant, on met à jour le statut
    const { error } = await supabase
      .from('contract_workflows')
      .update({
        payment_initiated: true,
        payment_initiated_at: new Date().toISOString()
      })
      .eq('unique_link', uniqueLink);

    if (error) throw error;

    // Simuler une URL de paiement - à remplacer par VoxPay
    message.info('Redirection vers le paiement...');
    return null; // Retourner l'URL VoxPay quand configuré
  };

  const getClientIP = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch {
      return 'unknown';
    }
  };

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={{ textAlign: 'center', color: '#fff' }}>
          <Spin size="large" />
          <p style={{ marginTop: '16px' }}>Chargement du contrat...</p>
        </div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <Result
            status="error"
            title="Contrat non trouvé"
            subTitle="Ce lien est invalide ou a expiré. Veuillez contacter le service commercial."
          />
        </div>
      </div>
    );
  }

  // Étapes selon le mode
  const getSteps = () => {
    if (mode === 'signature') {
      return [
        { title: 'Signature', icon: <EditOutlined /> },
        { title: 'Confirmé', icon: <CheckCircleOutlined /> },
      ];
    }
    if (mode === 'payment') {
      return [
        { title: 'Paiement', icon: <DollarOutlined /> },
        { title: 'Confirmé', icon: <CheckCircleOutlined /> },
      ];
    }
    // Mode unifié
    return [
      { title: 'Signature', icon: <EditOutlined /> },
      { title: 'Paiement', icon: <DollarOutlined /> },
      { title: 'Confirmé', icon: <CheckCircleOutlined /> },
    ];
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <Title level={2} style={styles.headerTitle}>
            {contract.client_name}
          </Title>
          <Text style={styles.headerSubtitle}>
            Contrat de location - {dayjs(contract.charter_date).format('DD MMMM YYYY')}
          </Text>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {/* Progress Steps */}
          <div style={styles.stepsContainer}>
            <Steps
              current={currentStep}
              items={getSteps()}
              size="small"
            />
          </div>

          {/* Contract Info */}
          <div style={styles.contractInfo}>
            <div style={styles.infoRow}>
              <Text type="secondary">Client</Text>
              <Text strong>{contract.client_name}</Text>
            </div>
            <div style={styles.infoRow}>
              <Text type="secondary">Date</Text>
              <Text strong>{dayjs(contract.charter_date).format('DD/MM/YYYY')}</Text>
            </div>
            <div style={styles.infoRow}>
              <Text type="secondary">Montant</Text>
              <Text strong style={{ color: '#1a1a1a', fontSize: '18px' }}>
                {contract.amount?.toLocaleString('fr-FR')} €
              </Text>
            </div>
            {contract.description && (
              <div style={{ ...styles.infoRow, borderBottom: 'none' }}>
                <Text type="secondary">Description</Text>
                <Text>{contract.description}</Text>
              </div>
            )}
          </div>

          {/* Signature Step */}
          {(mode === 'unified' || mode === 'signature') && currentStep === 0 && !signatureComplete && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>
                <EditOutlined style={{ marginRight: '8px' }} />
                Signature électronique
              </h3>

              {/* PDF Preview Link */}
              {contract.pdf_url && (
                <Alert
                  message="Document à signer"
                  description={
                    <a href={contract.pdf_url} target="_blank" rel="noopener noreferrer">
                      <FileTextOutlined /> Voir le contrat complet (PDF)
                    </a>
                  }
                  type="info"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
              )}

              {/* Billing Address Form */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px', color: '#1a1a1a' }}>
                  <HomeOutlined style={{ marginRight: '8px' }} />
                  Adresse de facturation
                </h4>
                <Form layout="vertical">
                  <Form.Item
                    label="Adresse"
                    required
                    style={{ marginBottom: '12px' }}
                  >
                    <Input
                      placeholder="123 rue de la Mer"
                      value={billingAddress.street}
                      onChange={(e) => handleAddressChange('street', e.target.value)}
                      size="large"
                    />
                  </Form.Item>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Code postal"
                        required
                        style={{ marginBottom: '12px' }}
                      >
                        <Input
                          placeholder="83990"
                          value={billingAddress.postalCode}
                          onChange={(e) => handleAddressChange('postalCode', e.target.value)}
                          size="large"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Ville"
                        required
                        style={{ marginBottom: '12px' }}
                      >
                        <Input
                          placeholder="Saint-Tropez"
                          value={billingAddress.city}
                          onChange={(e) => handleAddressChange('city', e.target.value)}
                          size="large"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item
                    label="Pays"
                    required
                    style={{ marginBottom: '16px' }}
                  >
                    <Input
                      placeholder="France"
                      value={billingAddress.country}
                      onChange={(e) => handleAddressChange('country', e.target.value)}
                      size="large"
                    />
                  </Form.Item>
                </Form>
              </div>

              <Divider style={{ margin: '16px 0' }} />

              {/* Checkboxes */}
              <div style={styles.checkboxGroup}>
                <Checkbox
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                >
                  <Text>
                    J'accepte les <a href="#terms">conditions générales de location</a> et
                    confirme avoir lu le contrat en intégralité.
                  </Text>
                </Checkbox>
                <Checkbox
                  checked={acceptCancellation}
                  onChange={(e) => setAcceptCancellation(e.target.checked)}
                >
                  <Text>
                    J'accepte la <a href="#cancellation">politique d'annulation</a> :
                    aucun remboursement en cas d'annulation moins de 48h avant.
                  </Text>
                </Checkbox>
              </div>

              {/* Signature Canvas */}
              <div style={styles.signatureBox}>
                <SignatureCanvas
                  ref={signatureRef}
                  canvasProps={{
                    style: styles.signatureCanvas,
                    className: 'signature-canvas'
                  }}
                  backgroundColor="#ffffff"
                />
              </div>
              <Button type="link" onClick={clearSignature} style={{ marginBottom: '16px' }}>
                Effacer la signature
              </Button>

              <Button
                type="primary"
                size="large"
                style={{ ...styles.actionButton, background: '#1a1a1a', borderColor: '#1a1a1a' }}
                onClick={handleSignature}
                loading={processing}
                disabled={!acceptTerms || !acceptCancellation || !isAddressValid()}
              >
                {processing ? 'Enregistrement...' : 'Signer le contrat'}
              </Button>
            </div>
          )}

          {/* Payment Step */}
          {mode === 'unified' && currentStep === 1 && signatureComplete && !paymentComplete && (
            <div style={styles.section}>
              <Result
                icon={<CheckCircleOutlined style={{ color: '#1a1a1a' }} />}
                title="Contrat signé !"
                subTitle="Procédez maintenant au paiement pour confirmer votre réservation."
              />

              <Divider />

              <h3 style={styles.sectionTitle}>
                <DollarOutlined style={{ marginRight: '8px' }} />
                Paiement sécurisé
              </h3>

              <Card style={{ marginBottom: '24px', textAlign: 'center' }}>
                <Title level={2} style={{ color: '#1a1a1a', margin: 0 }}>
                  {contract.amount?.toLocaleString('fr-FR')} €
                </Title>
                <Text type="secondary">Montant à régler</Text>
              </Card>

              <Button
                type="primary"
                size="large"
                style={{ ...styles.actionButton, background: '#1a1a1a', borderColor: '#1a1a1a' }}
                onClick={handlePayment}
                loading={processing}
              >
                {processing ? 'Redirection...' : 'Payer maintenant'}
              </Button>
            </div>
          )}

          {/* Payment Only Mode */}
          {mode === 'payment' && currentStep === 0 && !paymentComplete && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>
                <DollarOutlined style={{ marginRight: '8px' }} />
                Paiement sécurisé
              </h3>

              <Card style={{ marginBottom: '24px', textAlign: 'center' }}>
                <Title level={2} style={{ color: '#1a1a1a', margin: 0 }}>
                  {contract.amount?.toLocaleString('fr-FR')} €
                </Title>
                <Text type="secondary">Montant à régler</Text>
              </Card>

              <Button
                type="primary"
                size="large"
                style={{ ...styles.actionButton, background: '#1a1a1a', borderColor: '#1a1a1a' }}
                onClick={handlePayment}
                loading={processing}
              >
                {processing ? 'Redirection...' : 'Payer maintenant'}
              </Button>
            </div>
          )}

          {/* Signature Only - Confirmation */}
          {mode === 'signature' && signatureComplete && (
            <Result
              status="success"
              title="Signature enregistrée !"
              subTitle="Merci, votre contrat a été signé avec succès. Vous recevrez un email de confirmation."
            />
          )}

          {/* Payment Complete */}
          {(paymentComplete || (mode === 'unified' && currentStep === 2)) && (
            <Result
              status="success"
              title="Réservation confirmée !"
              subTitle="Merci pour votre confiance. Vous recevrez un email de confirmation avec tous les détails."
              extra={[
                <Button key="home" type="primary" style={{ background: '#1a1a1a', borderColor: '#1a1a1a' }}>
                  Retour à l'accueil
                </Button>
              ]}
            />
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Text type="secondary">
            Signature électronique sécurisée - {dayjs().format('DD/MM/YYYY HH:mm')}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            En signant ce document, vous acceptez que votre signature électronique
            a la même valeur juridique qu'une signature manuscrite.
          </Text>
        </div>
      </div>
    </div>
  );
};

export default ContractSignAndPay;
